import {
	createSlice,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import {
	getBalanceByPersonId,
	getOpenCommitmentByPersonId,
	getTransByPersonIdAndPeriodDate,
	postTrans as post,
} from "api";
import dayjs from "dayjs";
import { selectUserOrganisation } from "reducers/securitySlice";
import { WAITEMATA } from "../constants";

export const fetchBalanceByPersonId = createAsyncThunk(
	"balance/fetchBalanceByPersonId",
	//need the thunkApi's getState to retrieve auth token
	async (personId, { rejectWithValue, dispatch }) => {
		try {
			const data = await getBalanceByPersonId({ personId });
			dispatch(balanceReceived(data));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchOpenCommitmentByPersonId = createAsyncThunk(
	"balance/fetchOpenCommitmentByPersonId",
	//need the thunkApi's getState to retrieve auth token
	async (personId, { rejectWithValue, dispatch }) => {
		try {
			const data = await getOpenCommitmentByPersonId({ personId });
			dispatch(openCommitmentReceived(data));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const getFinancialEndDate = (org) => {
	const today = dayjs();

	//for waitemata, it's always end of calendar year
	if (org === WAITEMATA) return today.month(12).date(1).format("YYYY-MM-DD");

	let year;
	let month = "06";
	let day = "01";

	//for counties, must calculate financial year
	//months are zero indexed
	if (today.month() + 1 >= 7) year = today.year() + 1;
	else year = today.year();

	return `${year}-${month}-${day}`;
};

export const fetchTrans = createAsyncThunk(
	"balance/fetchTrans",
	//need the thunkApi's getState to retrieve auth token
	async (
		{ personId, periodDate },
		{ getState, rejectWithValue, dispatch }
	) => {
		try {
			const org = selectUserOrganisation(getState());
			const periodDate = getFinancialEndDate(org);
			const data = await getTransByPersonIdAndPeriodDate({
				personId,
				periodDate,
			});
			dispatch(transReceived(data));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postTrans = createAsyncThunk(
	"balance/postTrans",
	async (data, { dispatch, rejectWithValue }) => {
		try {
			const { personId } = data;
			await post(data);
			await dispatch(fetchBalanceByPersonId(personId));
			await dispatch(fetchTrans({ personId }));
			return true;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const initialState = {
	status: "starting",
	error: null,
	balance: [],
	trans: [],
	openCommitment: [],
};

const balanceSlice = createSlice({
	name: "balance",
	initialState,
	reducers: {
		balanceReceived(state, action) {
			state.balance = [...action.payload];
		},
		transReceived(state, action) {
			state.trans = [...action.payload];
		},
		openCommitmentReceived(state, action) {
			state.openCommitment = [...action.payload];
		},
		tranAdded(state, action) {
			state.trans.push(action.payload);
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			return initialState;
		},
		[fetchBalanceByPersonId.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchBalanceByPersonId.fulfilled]: (state, action) => {
			state.status = "idle";
		},
		[fetchBalanceByPersonId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postTrans.pending]: (state, action) => {
			state.status = "loading";
		},
		[postTrans.fulfilled]: (state, action) => {
			state.status = "idle";
		},
		[postTrans.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchOpenCommitmentByPersonId.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchOpenCommitmentByPersonId.fulfilled]: (state, action) => {
			state.status = "idle";
		},
		[fetchOpenCommitmentByPersonId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchTrans.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTrans.fulfilled]: (state, action) => {
			state.status = "idle";
		},
		[fetchTrans.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
	},
});

export const {
	balanceReceived,
	transReceived,
	openCommitmentReceived,
	tranAdded,
} = balanceSlice.actions;

export const selectBalanceStatus = (state) => state.cme.balance.status;
export const selectBalances = (state) => state.cme.balance.balance;

export const selectBalanceType = createSelector([selectBalances], (b) => {
	return b[0]?.type;
});

export const selectBalanceDollar = createSelector([selectBalances], (b) => {
	return b.find((i) => i.unit === "NZD")?.closeBalance || 0;
});
export const selectBalanceHours = createSelector([selectBalances], (b) => {
	return b.find((i) => ["Hours"].includes(i.unit))?.closeBalance || 0;
});
export const selectBalanceDays = createSelector([selectBalances], (b) => {
	return b.find((i) => ["Days", "DAYS"].includes(i.unit))?.closeBalance || 0;
});
export const selectTrans = (state) => state.cme.balance.trans;
export const selectTransDollar = createSelector([selectTrans], (t) =>
	t.filter((f) => f.unit === "NZD")
);
export const selectTransLeave = createSelector([selectTrans], (t) =>
	t.filter((f) => ["Days", "DAYS", "Hours"].includes(f.unit))
);

export const selectOpenCommitment = (state) => state.cme.balance.openCommitment;
export const selectOpenDollar = createSelector([selectOpenCommitment], (o) =>
	o.reduce((acc, val) => {
		return val.unit === "NZD" ? acc + val.openCommitment : acc;
	}, 0)
);
export const selectOpenLeave = createSelector([selectOpenCommitment], (o) =>
	o.reduce((acc, val) => {
		return ["Days", "DAYS", "Hours"].includes(val.unit)
			? acc + val.openCommitment
			: acc;
	}, 0)
);

export default balanceSlice.reducer;
